import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { groupService, memberService } from "@/services";
import type { CommissionType, GroupDraft, Member } from "@/services/types";
import {
  COMMISSION_TYPES,
  DAY_OPTIONS,
  DEFAULT_RESERVED_CYCLE,
  DIVIDEND_TIMING_OPTIONS,
} from "@/config/groups";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { Stepper } from "@/components/feature/Stepper";
import { TextField } from "@/components/base/TextField";
import { Select } from "@/components/base/Select";
import { CurrencyInput } from "@/components/currency/CurrencyInput";
import { DatePicker } from "@/components/base/DatePicker";
import { Toggle } from "@/components/base/Toggle";
import { RadioGroup } from "@/components/base/RadioGroup";
import { Card } from "@/components/base/Card";
import { Button } from "@/components/base/Button";
import { Alert } from "@/components/base/Alert";
import { EconomicsPreview } from "./EconomicsPreview";
import { chitValue, cyclePeriodLabel, durationMonths } from "@/utils/group";
import { CurrencyDisplay } from "@/components/currency/CurrencyDisplay";
import { formatDate } from "@/utils/format";

const defaultDraft = (): GroupDraft => ({
  name: "",
  monthlyInstallment: 0,
  memberCount: 0,
  startDate: "",
  auctionDay: 5,
  dueDay: 7,
  organizerMemberId: "",
  reservedCycle: DEFAULT_RESERVED_CYCLE,
  commissionType: "none",
  commissionValue: 0,
  minDiscountPct: null,
  maxDiscountPct: 25,
  // Must be > 0 — bids are validated as "a multiple of bidStep" (spec
  // section 11), which is undefined at 0. Matches the backend's own
  // default (kameti_groups.bid_step_amount DEFAULT 100.00) so a user who
  // never touches step 4 still submits a valid group.
  bidStep: 100,
  blockOverdueBidding: true,
  dividendTiming: "same_month",
  gracePeriodDays: 5,
  lateFee: 0,
  lateFeeIsPercent: false,
  lateFeePerDay: false,
});

const toInt = (v: string) => (v.trim() === "" ? NaN : parseInt(v, 10));

function toGroupDraft(group: Record<string, any>): GroupDraft {
  const { id: _id, groupCode: _c, chitValue: _v, status: _s, tickets: _t, wonCycles: _w, createdBy: _cb, createdAt: _ca, updatedBy: _ub, updatedAt: _ua, cancelledReason: _cr, ...draft } = group;
  return draft as GroupDraft;
}

export function CreateGroupWizard({ groupId }: { groupId?: string }) {
  const navigate = useNavigate();
  const { isViewer } = useAuth();
  const toast = useToast();

  const [draft, setDraft] = useState<GroupDraft>(defaultDraft);
  const [step, setStep] = useState(0);
  const [members, setMembers] = useState<Member[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(Boolean(groupId));

  useEffect(() => {
    let active = true;
    memberService.listMembers().then((m) => active && setMembers(m)).catch(() => { });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!groupId) return;
    let active = true;
    groupService
      .getGroup(groupId)
      .then((g) => {
        if (!active) return;
        setDraft(toGroupDraft(g));
      })
      .catch((err) => toast.error("Could not load group", err instanceof Error ? err.message : "Please try again."))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [groupId, toast]);

  const set = useCallback(<K extends keyof GroupDraft>(key: K, value: GroupDraft[K]) => {
    setDraft((d) => ({ ...d, [key]: value }));
  }, []);

  const organizerOptions = useMemo(
    () =>
      members
        .filter((m) => m.status !== "blacklisted")
        .map((m) => ({ value: m.id, label: `${m.fullName} (${m.memberCode})` })),
    [members]
  );

  const duration = durationMonths(draft.memberCount || 0);
  const pool = chitValue(draft.monthlyInstallment || 0, draft.memberCount || 0);

  const validateStep = useCallback(
    (index: number): string | null => {
      switch (index) {
        case 0:
          if (!draft.name.trim()) return "Give the group a name.";
          if (!(draft.monthlyInstallment > 0)) return "Monthly installment must be greater than zero.";
          if (!(draft.memberCount >= 2)) return "A group needs at least two members.";
          return null;
        case 1:
          if (!draft.startDate) return "Choose a start date for the first cycle.";
          return null;
        case 2:
          if (!draft.organizerMemberId) return "Select which member acts as the organizer.";
          if (draft.memberCount > 0 && (draft.reservedCycle < 1 || draft.reservedCycle > draft.memberCount))
            return `The reserved cycle must be between 1 and ${draft.memberCount}.`;
          if (draft.commissionType === "pool_percentage" || draft.commissionType === "discount_percentage") {
            if (!(draft.commissionValue > 0)) return "Enter a commission percentage greater than zero.";
            if (draft.commissionValue > 100) return "A percentage commission cannot exceed 100.";
          }
          if (draft.commissionType === "fixed" && !(draft.commissionValue > 0)) return "Enter a fixed commission amount greater than zero.";
          return null;
        case 3:
          if (draft.maxDiscountPct == null || isNaN(draft.maxDiscountPct) || draft.maxDiscountPct < 0 || draft.maxDiscountPct > 100)
            return "Maximum discount percentage is required and must be between 0 and 100.";
          if (draft.minDiscountPct != null && draft.maxDiscountPct < draft.minDiscountPct)
            return "The maximum discount cannot be lower than the minimum.";
          if (!draft.bidStep || draft.bidStep <= 0)
            return "Bid step amount is required and must be greater than zero.";
          return null;
        case 4:
          if (draft.gracePeriodDays < 0) return "Grace period cannot be negative.";
          if (draft.lateFee < 0) return "Late fee cannot be negative.";
          return null;
        default:
          return null;
      }
    },
    [draft]
  );

  const doCreate = useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      if (groupId) {
        const updated = await groupService.updateGroup(groupId, draft, "Staff User");
        toast.success("Group updated", `${updated.name} (${updated.groupCode}) was saved.`);
        navigate(`/groups/${updated.id}`);
      } else {
        const created = await groupService.createGroup(draft, "Staff User");
        toast.success("Group created", `${created.name} (${created.groupCode}) is ready for enrolment.`);
        navigate(`/groups/${created.id}/enrollment`);
      }
    } catch (err) {
      toast.error(groupId ? "Could not update group" : "Could not create group", err instanceof Error ? err.message : "Please try again.");
      setSubmitting(false);
    }
  }, [draft, navigate, submitting, toast, groupId]);

  const steps = [
    {
      id: "basic",
      title: "Basic details",
      subtitle: "Name, size and installment",
      content: (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextField label="Group name" required value={draft.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Lakshmi-10" />
          <div className="relative">
            <CurrencyInput
              label="Monthly installment"
              required
              value={draft.monthlyInstallment || null}
              onChange={(v) => set("monthlyInstallment", v ?? 0)}
              placeholder="e.g. 5,000"
              helper="Every member pays this amount each month."
            />
          </div>
          <TextField
            label="Total number of members"
            required
            inputMode="numeric"
            value={draft.memberCount ? String(draft.memberCount) : ""}
            onChange={(e) => set("memberCount", toInt(e.target.value) || 0)}
            placeholder="e.g. 20"
          />
          <div>
            <TextField label="Group code" value="" readOnly placeholder="Auto-generated" helper="Assigned automatically when the group is created." />
          </div>
          <div className="sm:col-span-2 flex flex-wrap gap-6 rounded-md border border-background-200 bg-background-100/40 px-4 py-3">
            <div>
              <p className="text-xs text-foreground-500">Duration (computed)</p>
              <p className="tnum text-sm font-semibold text-foreground-900">{duration} months</p>
            </div>
            <div>
              <p className="text-xs text-foreground-500">Chit value (computed)</p>
              <p className="text-sm font-semibold text-foreground-900"><CurrencyDisplay amount={pool} align="left" /></p>
            </div>
            <p className="self-center text-xs text-foreground-500">
              The number of monthly cycles always equals the number of members, so the duration and chit value are fixed by arithmetic.
            </p>
          </div>
        </div>
      ),
    },
    {
      id: "schedule",
      title: "Schedule",
      subtitle: "Dates and days of month",
      content: (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <DatePicker label="Start date" required value={draft.startDate} onChange={(v) => set("startDate", v)} />
          <Select
            label="Auction day of month"
            value={String(draft.auctionDay)}
            onChange={(v) => set("auctionDay", parseInt(v, 10))}
            options={DAY_OPTIONS}
            helper="Auctions are held on this day each month."
          />
          <Select
            label="Installment due day"
            value={String(draft.dueDay)}
            onChange={(v) => set("dueDay", parseInt(v, 10))}
            options={DAY_OPTIONS}
            helper="Installments fall due on this day."
          />
          <div className="lg:col-span-3 flex flex-wrap gap-6 rounded-md border border-background-200 bg-background-100/40 px-4 py-3">
            <div>
              <p className="text-xs text-foreground-500">First cycle month</p>
              <p className="text-sm font-semibold text-foreground-900">{draft.startDate ? cyclePeriodLabel(draft.startDate, 1) : "—"}</p>
            </div>
            <div>
              <p className="text-xs text-foreground-500">Final cycle month</p>
              <p className="text-sm font-semibold text-foreground-900">{draft.startDate ? cyclePeriodLabel(draft.startDate, duration) : "—"}</p>
            </div>
            <p className="self-center text-xs text-foreground-500">
              {draft.startDate ? `The group runs for ${duration} months and closes in ${cyclePeriodLabel(draft.startDate, duration)}.` : "Pick a start date to see when this group ends."}
            </p>
          </div>
        </div>
      ),
    },
    {
      id: "organizer",
      title: "Organizer & commission",
      subtitle: "Who runs the group",
      content: (
        <div className="space-y-5">
          <Select
            label="Organizer member"
            required
            searchable
            value={draft.organizerMemberId || null}
            onChange={(v) => set("organizerMemberId", v)}
            placeholder="Search and select a member…"
            options={organizerOptions}
            helper="This person holds a ticket and pays installments like every other member."
          />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <TextField
              label="Reserved cycle"
              inputMode="numeric"
              value={String(draft.reservedCycle)}
              onChange={(e) => set("reservedCycle", toInt(e.target.value) || 1)}
              helper={draft.memberCount > 0 ? `Valid range: 1–${draft.memberCount}` : "Set the member count first"}
            />
          </div>
          <RadioGroup
            label="Commission type"
            value={draft.commissionType}
            onChange={(v) => set("commissionType", v as CommissionType)}
            options={COMMISSION_TYPES.map((c) => ({
              value: c.value,
              label: (
                <span>
                  {c.label}
                  <span className="ml-1 text-xs text-foreground-500">{c.helper}</span>
                </span>
              ),
            }))}
          />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {draft.commissionType === "pool_percentage" || draft.commissionType === "discount_percentage" ? (
              <TextField
                label="Commission percentage"
                inputMode="decimal"
                value={draft.commissionValue ? String(draft.commissionValue) : ""}
                onChange={(e) => set("commissionValue", parseFloat(e.target.value) || 0)}
                placeholder="e.g. 5"
                helper="0–100"
              />
            ) : null}
            {draft.commissionType === "fixed" ? (
              <CurrencyInput label="Fixed amount per cycle" value={draft.commissionValue || null} onChange={(v) => set("commissionValue", v ?? 0)} />
            ) : null}
          </div>
          <Alert variant="info">
            The organizer's earning is normally the reserved full-pool cycle rather than a separate commission. Commission is optional.
          </Alert>
        </div>
      ),
    },
    {
      id: "bidding",
      title: "Bidding rules",
      subtitle: "Auction caps and increments",
      content: (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <TextField
            label="Minimum discount (%)"
            inputMode="decimal"
            value={draft.minDiscountPct != null ? String(draft.minDiscountPct) : ""}
            onChange={(e) => set("minDiscountPct", e.target.value === "" ? null : parseFloat(e.target.value))}
            placeholder="Optional"
            helper="Optional lower cap (leave blank for none)."
          />
          <TextField
            label="Maximum discount (%) *"
            inputMode="decimal"
            value={draft.maxDiscountPct != null ? String(draft.maxDiscountPct) : ""}
            onChange={(e) => set("maxDiscountPct", e.target.value === "" ? null : parseFloat(e.target.value))}
            placeholder="e.g. 25"
            helper="Required upper cap on auction discount (0-100%)."
          />
          <TextField
            label="Bid step amount *"
            inputMode="decimal"
            value={draft.bidStep ? String(draft.bidStep) : ""}
            onChange={(e) => set("bidStep", parseFloat(e.target.value) || 0)}
            placeholder="e.g. 500"
            helper="Required increment for bids (must be > 0)."
          />
          <div className="sm:col-span-2 lg:col-span-3">
            <Toggle
              checked={draft.blockOverdueBidding}
              onChange={(v) => set("blockOverdueBidding", v)}
              label="Block members with overdue dues from bidding"
              description="Members who have not paid will be excluded from the auction until cleared."
            />
          </div>
        </div>
      ),
    },
    {
      id: "dividend",
      title: "Dividend & penalty",
      subtitle: "Collection timing and late fees",
      content: (
        <div className="space-y-5">
          <RadioGroup
            label="Dividend adjusts"
            value={draft.dividendTiming}
            onChange={(v) => set("dividendTiming", v as GroupDraft["dividendTiming"])}
            options={DIVIDEND_TIMING_OPTIONS.map((d) => ({ value: d.value, label: <span>{d.label}<span className="ml-1 text-xs text-foreground-500">{d.helper}</span></span> }))}
          />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <TextField
              label="Grace period (days)"
              inputMode="numeric"
              value={String(draft.gracePeriodDays)}
              onChange={(e) => set("gracePeriodDays", toInt(e.target.value) || 0)}
            />
            <CurrencyInput label="Late fee" value={draft.lateFee || null} onChange={(v) => set("lateFee", v ?? 0)} />
            <div className="flex flex-col justify-end gap-3">
              {/* <Toggle checked={draft.lateFeeIsPercent} onChange={(v) => set("lateFeeIsPercent", v)} label="Fee is a percentage" size="sm" /> */}
              <Toggle checked={draft.lateFeePerDay} onChange={(v) => set("lateFeePerDay", v)} label="Accrues per day" size="sm" />
            </div>
          </div>
        </div>
      ),
    },
    {
      id: "review",
      title: "Review & create",
      subtitle: "Confirm every setting",
      content: (
        <ReviewSection
          draft={draft}
          duration={duration}
          pool={pool}
          organizerName={organizerOptions.find((o) => o.value === draft.organizerMemberId)?.label ?? "—"}
          onEditStep={setStep}
        />
      ),
    },
  ];

  if (loading) {
    return (
      <div className="space-y-3">
        <div className="h-40 animate-pulse rounded-lg bg-background-200" />
        <div className="h-40 animate-pulse rounded-lg bg-background-200" />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
      <div className="min-w-0 xl:col-span-7 2xl:col-span-8">
        <Stepper
          steps={steps}
          validateStep={validateStep}
          onComplete={doCreate}
          completeLabel={groupId ? "Save changes" : isViewer ? "Create" : "Create group"}
          currentStep={step}
          onStepChange={setStep}
        />
      </div>
      <div className="min-w-0 xl:col-span-5 2xl:col-span-4">
        <div className="xl:sticky xl:top-4">
          <EconomicsPreview
            monthlyInstallment={draft.monthlyInstallment}
            memberCount={draft.memberCount}
            reservedCycle={draft.reservedCycle}
          />
        </div>
      </div>
    </div>
  );
}

interface ReviewRowProps {
  label: string;
  value: React.ReactNode;
}

function ReviewRow({ label, value }: ReviewRowProps) {
  return (
    <div className="flex items-start justify-between gap-4 py-1.5">
      <span className="text-xs text-foreground-500">{label}</span>
      <span className="text-right text-sm font-medium text-foreground-900">{value}</span>
    </div>
  );
}

function Section({ title, onEdit, children }: { title: string; onEdit?: () => void; children: React.ReactNode }) {
  return (
    <div>
      <div className="flex items-center justify-between border-b border-background-100 pb-1.5">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-foreground-500">{title}</h4>
        {onEdit ? (
          <button type="button" onClick={onEdit} className="cursor-pointer text-xs font-medium text-primary-600 hover:underline">
            Edit
          </button>
        ) : null}
      </div>
      <div className="pt-1.5">{children}</div>
    </div>
  );
}

function ReviewSection({
  draft,
  duration,
  pool,
  organizerName,
  onEditStep,
}: {
  draft: GroupDraft;
  duration: number;
  pool: number;
  organizerName: string;
  onEditStep: (s: number) => void;
}) {
  const commissionLabel = COMMISSION_TYPES.find((c) => c.value === draft.commissionType)?.label ?? draft.commissionType;
  const commissionValue =
    draft.commissionType === "none"
      ? "—"
      : draft.commissionType === "fixed"
        ? <CurrencyDisplay amount={draft.commissionValue} hideSymbol />
        : `${draft.commissionValue}%`;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <Section title="Basic details" onEdit={() => onEditStep(0)}>
          <ReviewRow label="Group name" value={draft.name} />
          <ReviewRow label="Installment" value={<CurrencyDisplay amount={draft.monthlyInstallment} hideSymbol />} />
          <ReviewRow label="Members" value={draft.memberCount} />
          <ReviewRow label="Duration" value={`${duration} months`} />
          <ReviewRow label="Chit value" value={<CurrencyDisplay amount={pool} hideSymbol />} />
        </Section>
        <Section title="Schedule" onEdit={() => onEditStep(1)}>
          <ReviewRow label="Start date" value={draft.startDate ? formatDate(draft.startDate) : "—"} />
          <ReviewRow label="Auction day" value={`${draft.auctionDay}th`} />
          <ReviewRow label="Due day" value={`${draft.dueDay}th`} />
        </Section>
        <Section title="Organizer & commission" onEdit={() => onEditStep(2)}>
          <ReviewRow label="Organizer" value={organizerName} />
          <ReviewRow label="Reserved cycle" value={`Cycle ${draft.reservedCycle}`} />
          <ReviewRow label="Commission" value={commissionLabel} />
          <ReviewRow label="Commission value" value={commissionValue} />
        </Section>
        <Section title="Bidding rules" onEdit={() => onEditStep(3)}>
          <ReviewRow label="Min discount" value={draft.minDiscountPct != null ? `${draft.minDiscountPct}%` : "None"} />
          <ReviewRow label="Max discount" value={draft.maxDiscountPct != null ? `${draft.maxDiscountPct}%` : "—"} />
          <ReviewRow label="Bid step" value={<CurrencyDisplay amount={draft.bidStep} hideSymbol />} />
          <ReviewRow label="Overdue blocked" value={draft.blockOverdueBidding ? "Yes" : "No"} />
        </Section>
      </div>
      <Section title="Dividend & penalty" onEdit={() => onEditStep(4)}>
        <div className="grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-3">
          <ReviewRow label="Dividend timing" value={draft.dividendTiming === "same_month" ? "Same month" : "Following month"} />
          <ReviewRow label="Grace period" value={`${draft.gracePeriodDays} days`} />
          <ReviewRow label="Late fee" value={`${draft.lateFeeIsPercent ? `${draft.lateFee}%` : `₹${draft.lateFee.toFixed(2)}`}${draft.lateFeePerDay ? " / day" : ""}`} />
        </div>
      </Section>
    </div>
  );
}